import React from "react";
import { ArrowRight, CircleDot, GitBranch, Route } from "lucide-react";
import { Reveal } from "./Shared";
import { productUrl } from "@/config/siteUrls";

const dependencySteps = [
  {
    title: "Trigger or Future Demand",
    body: "An external demand, internal sensation, conditioned cue, or dependency-generated signal enters the system.",
  },
  {
    title: "Learned Adaptive Pattern",
    body: "The nervous system moves toward the response it has already learned, rehearsed, and made efficient.",
  },
  {
    title: "Sympathetic Activation",
    body: "Energy, attention, muscular readiness, and behavioural urgency increase to meet the perceived demand.",
  },
  {
    title: "Emotional Reactivity",
    body: "The mobilized state is experienced as urgency, agitation, anger, anxiety, sadness, frustration, or another strong emotional response.",
  },
  {
    title: "Outsourced Relief",
    body: "The person changes the state through a rapidly available external source of relief, such as compulsive scrolling, avoidance, or another dependency-producing behaviour.",
  },
  {
    title: "Dependency Reinforcement",
    body: "Temporary relief teaches the system to return to the same external route. Reinforcement can then re-enter as a Trigger or Future Demand, the Learned Adaptive Pattern, Sympathetic Activation, or Emotional Reactivity.",
  },
];

const adaptiveSteps = [
  {
    title: "Future Demand",
    body: "A real demand enters the system. Stress and activation are not removed from life.",
  },
  {
    title: "Sympathetic Activation",
    body: "The nervous system mobilizes energy and attention. This is a normal response to challenge, not a failure.",
  },
  {
    title: "Emotional Reactivity",
    body: "The person notices how the activation is shaping emotion, attention, and behavioural urgency.",
  },
  {
    title: "Matched Adaptive Intervention",
    body: "A physiological input is selected according to the person’s state, capacity, and position in the loop.",
  },
  {
    title: "Parasympathetic Recovery",
    body: "The system is given an opportunity to disengage from sustained mobilization and support restoration.",
  },
  {
    title: "Recovery and Homeostasis",
    body: "The activation–recovery cycle completes and the system returns toward a functional regulatory range.",
  },
];

const StepList = ({ steps, dark = false }) => (
  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
    {steps.map((step, index) => (
      <div key={step.title} className="flex gap-4">
        <div
          className="shrink-0 mt-0.5 h-8 w-8 rounded-full flex items-center justify-center text-[10px] font-semibold"
          style={
            dark
              ? {
                  color: "#F4F1EA",
                  border: "1px solid #454C50",
                  backgroundColor: "#1A1D20",
                }
              : {
                  color: "var(--forr-teal-deep)",
                  border: "1px solid var(--forr-teal-soft)",
                  backgroundColor: "var(--forr-teal-tint)",
                }
          }
        >
          {String(index + 1).padStart(2, "0")}
        </div>
        <div>
          <h4
            className="font-display text-base font-medium"
            style={{ color: dark ? "#F4F1EA" : "var(--forr-ink)" }}
          >
            {step.title}
          </h4>
          <p
            className="mt-2 text-sm font-light leading-relaxed"
            style={{ color: dark ? "#AEB4B7" : "var(--forr-ink-soft)" }}
          >
            {step.body}
          </p>
        </div>
      </div>
    ))}
  </div>
);

const LoopFramework = () => (
  <section
    id="two-loops"
    data-testid="two-loops-section"
    className="relative border-t forr-canvas overflow-hidden"
    style={{ borderColor: "var(--forr-line-soft)" }}
  >
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-28 sm:py-36">
      <Reveal>
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-3">
            <span className="forr-hairline" />
            <span className="forr-overline">The nervous system under demand</span>
          </div>
          <h2 className="mt-8 font-display forr-text-ink text-4xl sm:text-5xl md:text-6xl font-medium tracking-[-0.03em] leading-[1.04]">
            Two loops can run inside the{" "}
            <span className="forr-text-teal-deep">same nervous system.</span>
          </h2>
          <p className="mt-8 max-w-3xl text-lg font-light forr-text-soft leading-relaxed">
            Both begin with a system responding to demand. What differs is where
            the activation goes, what produces relief, and what the nervous
            system learns to repeat next.
          </p>
        </div>
      </Reveal>

      <div className="mt-20 grid gap-8">
        <Reveal>
          <article
            className="rounded-[30px] sm:rounded-[36px] p-7 sm:p-10 lg:p-14"
            style={{
              background:
                "linear-gradient(145deg, #171A1C 0%, #0A0B0C 72%, #050606 100%)",
              border: "1px solid #30363A",
              boxShadow: "0 34px 100px -54px rgba(0,0,0,0.95)",
            }}
          >
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
              <div className="lg:col-span-4">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#929A9F]">
                  The reinforced route
                </div>
                <h3 className="mt-5 font-display text-3xl sm:text-4xl font-medium tracking-[-0.02em] text-[#F4F1EA]">
                  The Dependency Loop
                </h3>
                <p className="mt-6 text-base font-light leading-relaxed text-[#AEB4B7]">
                  The Dependency Loop forms when a learned response repeatedly
                  moves sympathetic activation and emotional reactivity toward
                  outsourced relief. The relief is real enough to reinforce the
                  route, but it does not reliably develop the person’s own
                  capacity to complete recovery.
                </p>
                <div className="mt-8 flex items-start gap-3 rounded-2xl border border-[#30363A] bg-[#141719] p-5">
                  <GitBranch
                    size={18}
                    strokeWidth={1.5}
                    className="mt-0.5 shrink-0 text-[#C3C9CC]"
                  />
                  <p className="text-sm font-light leading-relaxed text-[#AEB4B7]">
                    Dependency reinforcement can feed back into a trigger or
                    future demand, the learned pattern, sympathetic activation,
                    or emotional reactivity—so the system no longer needs one
                    obvious outside trigger to restart.
                  </p>
                </div>
              </div>
              <div className="lg:col-span-8">
                <StepList steps={dependencySteps} dark />
              </div>
            </div>
          </article>
        </Reveal>

        <Reveal>
          <article
            className="rounded-[30px] sm:rounded-[36px] p-7 sm:p-10 lg:p-14 forr-surface"
            style={{
              border: "1px solid var(--forr-line)",
              boxShadow: "var(--forr-shadow-lg)",
            }}
          >
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
              <div className="lg:col-span-4">
                <div className="forr-overline">The recovery route</div>
                <h3 className="mt-5 font-display forr-text-ink text-3xl sm:text-4xl font-medium tracking-[-0.02em]">
                  The Adaptive Loop
                </h3>
                <p className="mt-6 text-base font-light forr-text-soft leading-relaxed">
                  The Adaptive Loop does not eliminate demand, activation, or
                  emotion. It introduces a matched physiological input that gives
                  the activated system another route—one directed toward
                  recovery and homeostatic regulation.
                </p>
                <div
                  className="mt-8 flex items-start gap-3 rounded-2xl p-5"
                  style={{
                    backgroundColor: "var(--forr-teal-tint)",
                    border: "1px solid var(--forr-teal-soft)",
                  }}
                >
                  <Route
                    size={18}
                    strokeWidth={1.5}
                    className="mt-0.5 shrink-0 forr-text-teal-deep"
                  />
                  <p className="text-sm font-light leading-relaxed forr-text-soft">
                    Homeostasis means returning toward a functional regulatory
                    range—not remaining permanently calm.
                  </p>
                </div>
              </div>
              <div className="lg:col-span-8">
                <StepList steps={adaptiveSteps} />
              </div>
            </div>
          </article>
        </Reveal>
      </div>

      <Reveal>
        <div
          className="mt-10 rounded-[30px] sm:rounded-[36px] overflow-hidden"
          style={{
            border: "1px solid var(--forr-line)",
            boxShadow: "var(--forr-shadow-md)",
          }}
        >
          <div className="grid lg:grid-cols-2">
            <div className="p-8 sm:p-10 lg:p-14 forr-canvas-alt">
              <div className="inline-flex items-center gap-3">
                <CircleDot
                  size={18}
                  strokeWidth={1.5}
                  className="forr-text-teal-deep"
                />
                <span className="forr-overline">Why disruption matters</span>
              </div>
              <h3 className="mt-6 font-display forr-text-ink text-3xl sm:text-4xl font-medium tracking-[-0.025em] leading-tight">
                The goal is not to suppress the state. It is to change where the
                state goes next.
              </h3>
              <p className="mt-6 text-base font-light forr-text-soft leading-relaxed">
                The Dependency Loop is not simply a bad decision. It is a learned
                physiological and behavioural route that works quickly enough to
                keep being selected. Disruption introduces another response while
                the pattern is active, so the nervous system can experience a
                different consequence.
              </p>
            </div>
            <div className="p-8 sm:p-10 lg:p-14 forr-surface flex flex-col justify-center">
              <div className="font-display text-xl sm:text-2xl forr-text-ink font-medium leading-relaxed">
                Dependency pathway
                <span className="mx-3 forr-text-faint">→</span>
                <span className="forr-text-teal-deep">
                  matched physiological intervention
                </span>
                <span className="mx-3 forr-text-faint">→</span>
                adaptive recovery pathway
              </div>
              <p className="mt-6 text-sm font-light forr-text-soft leading-relaxed">
                Repetition matters. Each completed adaptive cycle gives the
                system more evidence that activation can be influenced, completed,
                and recovered from without depending exclusively on outsourced
                relief.
              </p>
              <a
                href={productUrl("/assessment/start")}
                className="forr-btn-primary group mt-9 inline-flex w-fit items-center gap-3 rounded-full px-7 py-4 text-sm font-semibold tracking-wide"
              >
                Assess your state. Break the loop.
                <ArrowRight
                  size={16}
                  strokeWidth={2}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </a>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default LoopFramework;
